import React from 'react';
import {
  Alert, Badge, Button, ErrorState, SkeletonCard,
} from '@parsina/framework';
import { Merge, X } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { EMPTY } from '../../lib/format';
import {
  AttributeBadge, EnabledBadge, Ident, accountName, dropBlockers, refusalText,
} from './shared';

/**
 * Two accounts side by side (`accountConsolidationPreview`), the choice of which
 * one stays, and the way into the consolidation.
 *
 * The orientation is the operator's: «این حساب بماند» on the other card swaps it
 * and asks the server again. A swap that would drop a doctor's or a staff
 * member's account is not offered — the button is disabled and says why, in
 * words, beside it (DESIGN_CONCEPT §1.3: the reason, not a mystery). The server's
 * refusals for the CURRENT orientation are listed above the action, and the
 * action is disabled while there are any.
 *
 * Its own four states: a card skeleton while the pair loads, `ErrorState` in its
 * place on failure, the two cards on content (there is no «empty»: two ids
 * always name two accounts, or the query fails NOT_FOUND).
 */
export default function ConsolidationPreview({
  status, preview, error, onRetry, onKeep, onConsolidate, onClose,
}) {
  const { t, has } = useTranslation();

  return (
    <section
      className="mt-4 rounded-lg border border-border bg-card p-4"
      aria-labelledby="accounts-preview-title"
      data-testid="accounts-preview"
    >
      <div className="mb-3 flex items-center gap-3">
        <h2 id="accounts-preview-title" className="text-base font-bold text-foreground">
          {t('accounts.preview.title')}
        </h2>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="accounts-preview-close">
          <X size={14} aria-hidden />
          {t('accounts.preview.close')}
        </Button>
      </div>

      {status === 'loading' ? (
        <div
          className="grid gap-4 md:grid-cols-2"
          role="status"
          aria-busy="true"
          aria-label={t('accounts.preview.loading')}
          data-testid="accounts-preview-loading"
          data-state="loading"
        >
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : status === 'error' ? (
        <ErrorState
          title={t('accounts.preview.error')}
          description={error}
          onRetry={onRetry}
          retryLabel={t('common.actions.retry')}
          data-testid="accounts-preview-error"
        />
      ) : preview ? (
        <div data-state="content">
          <div className="grid gap-4 md:grid-cols-2">
            <AccountPanel account={preview.keep} other={preview.drop} role="keep" onKeep={onKeep} />
            <AccountPanel account={preview.drop} other={preview.keep} role="drop" onKeep={onKeep} />
          </div>

          {!preview.formerPhoneNumberKept ? (
            <Alert variant="warning" className="mt-3" data-testid="accounts-preview-former-not-kept">
              {t('accounts.preview.formerNotKept')}
            </Alert>
          ) : null}

          {preview.refusals?.length ? (
            <Alert variant="error" title={t('accounts.preview.refusedTitle')} className="mt-3" data-testid="accounts-preview-refusals">
              <ul className="list-disc ps-5">
                {preview.refusals.map((r) => (
                  <li key={r.code} data-testid={`accounts-refusal-${r.code}`}>{refusalText(t, has, r)}</li>
                ))}
              </ul>
            </Alert>
          ) : null}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              variant="destructive"
              disabled={!preview.allowed}
              aria-describedby={!preview.allowed ? 'accounts-preview-blocked' : undefined}
              onClick={onConsolidate}
              data-testid="accounts-consolidate-open"
            >
              <Merge size={16} aria-hidden />
              {t('accounts.preview.consolidate')}
            </Button>
            <span className="text-sm text-muted-foreground" id="accounts-preview-blocked">
              {preview.allowed
                ? t('accounts.preview.summary', { keep: accountName(preview.keep), drop: accountName(preview.drop) })
                : t('accounts.preview.blocked')}
            </span>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** One account's card: what it is, what it holds here, and whether it can stay. */
function AccountPanel({
  account, other, role, onKeep,
}) {
  const { t, has, formatDate } = useTranslation();
  const keeping = role === 'keep';
  // Keeping THIS one drops the other; a doctor's or staff account is never dropped.
  const blockers = keeping ? [] : dropBlockers(other);
  const reasonId = `accounts-keep-blocked-${account.id}`;

  return (
    <article
      className={`rounded-lg border p-4 ${keeping ? 'border-primary' : 'border-border'}`}
      aria-label={t(keeping ? 'accounts.preview.keepAria' : 'accounts.preview.dropAria', { name: accountName(account) })}
      data-testid={`accounts-preview-${role}`}
      data-account-id={account.id}
    >
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <span dir="auto" className="min-w-0 truncate font-semibold text-foreground">{accountName(account)}</span>
        <Badge variant={keeping ? 'success' : 'destructive'} data-testid={`accounts-preview-${role}-role`}>
          {keeping ? t('accounts.preview.stays') : t('accounts.preview.goes')}
        </Badge>
      </header>

      <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-3 gap-y-1.5 text-sm">
        <dt className="text-muted-foreground">{t('accounts.fields.username')}</dt>
        <dd><Ident value={account.username} /></dd>
        <dt className="text-muted-foreground">{t('accounts.fields.status')}</dt>
        <dd><EnabledBadge enabled={account.enabled} /></dd>
        <dt className="text-muted-foreground">{t('accounts.fields.phone')}</dt>
        <dd className="flex flex-wrap items-center gap-1.5">
          <Ident value={account.phoneNumber} data-testid={`accounts-preview-${role}-phone`} />
          {account.phoneNumber ? (
            <span className="text-xs text-muted-foreground">
              {account.phoneVerified ? t('accounts.account.verified') : t('accounts.account.unverified')}
            </span>
          ) : null}
        </dd>
        <dt className="text-muted-foreground">{t('accounts.fields.formerPhone')}</dt>
        <dd><Ident value={account.formerPhoneNumber} /></dd>
        <dt className="text-muted-foreground">{t('accounts.fields.nationalId')}</dt>
        <dd><Ident value={account.nationalId} /></dd>
        <dt className="text-muted-foreground">{t('accounts.fields.roles')}</dt>
        <dd data-testid={`accounts-preview-${role}-roles`}><RoleList account={account} /></dd>
        <dt className="text-muted-foreground">{t('accounts.fields.practitioner')}</dt>
        <dd>
          {account.practitionerClinics == null
            ? t('accounts.account.practitionerUnknown')
            : account.practitionerClinics > 0
              ? t('accounts.account.practitionerAt', { count: account.practitionerClinics })
              : t('accounts.account.practitionerNone')}
        </dd>
        <dt className="text-muted-foreground">{t('accounts.fields.records')}</dt>
        <dd data-testid={`accounts-preview-${role}-records`}>
          {account.records?.length ? account.records.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-1.5">
              <span dir="auto">{r.fullName || t('accounts.records.unnamed')}</span>
              <Ident value={r.id} />
            </div>
          )) : t('accounts.records.noneHere')}
        </dd>
        <dt className="text-muted-foreground">{t('accounts.fields.attribute')}</dt>
        <dd className="flex flex-wrap items-center gap-1.5">
          <AttributeBadge attribute={account.patientIdAttribute} />
          {account.patientIdAttribute ? <Ident value={account.patientIdAttribute.value} /> : null}
        </dd>
        <dt className="text-muted-foreground">{t('accounts.fields.createdAt')}</dt>
        <dd>{account.createdAt ? formatDate(account.createdAt) : EMPTY}</dd>
      </dl>

      <div className="mt-4">
        <Button
          size="sm"
          variant={keeping ? 'default' : 'outline'}
          aria-pressed={keeping}
          disabled={keeping || blockers.length > 0}
          aria-describedby={blockers.length ? reasonId : undefined}
          onClick={() => onKeep(account.id)}
          data-testid={`accounts-keep-${account.id}`}
        >
          {keeping ? t('accounts.preview.kept') : t('accounts.preview.keepThis')}
        </Button>
        {blockers.length ? (
          <p id={reasonId} className="mt-1.5 text-xs text-[var(--chip-danger-ink)]" data-testid={`accounts-keep-reason-${account.id}`}>
            {t('accounts.preview.keepBlocked', { name: accountName(other) })}
            {' '}
            {blockers.map((code) => refusalText(t, has, { code })).join(' ')}
          </p>
        ) : null}
      </div>
    </article>
  );
}

/** Roles as chips — a non-patient one marked — plus groups and a clinic id. */
export function RoleList({ account }) {
  const { t } = useTranslation();
  const nonPatient = new Set(account.nonPatientRoles || []);
  const roles = account.roles || [];
  if (!roles.length && !account.groups?.length && !account.clinicId) return <span>{EMPTY}</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <Badge key={r} variant={nonPatient.has(r) ? 'warning' : 'outline'} dir="ltr">{r}</Badge>
      ))}
      {(account.groups || []).map((g) => (
        <Badge key={`g:${g}`} variant="warning" title={t('accounts.account.group')} dir="ltr">{g}</Badge>
      ))}
      {account.clinicId ? (
        <Badge variant="warning" title={t('accounts.account.clinic')} dir="ltr">
          {`clinic_id: ${account.clinicId}`}
        </Badge>
      ) : null}
    </span>
  );
}
